import {Card, Column, Table} from 'platform/components';
import {Box, Center, getStepsFromPixels, Heading, Show, Space, Spinner} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {FastTruckIssue, PatchReceiveNoteItem, ReceiveNoteItem} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {FastTrackIssueTableRow} from './FastTrackIssueTableRow';
import {FastTrackTableHeader} from './FastTrackTableHeader';
import {ReceiveNoteInformationRow} from './ReceiveNoteInformationRow';

export interface FastTrackIssuesCardProps {
  receiveNoteItem: ReceiveNoteItem;
  isCollapsedAll: boolean;
  onAcceptIssue: (item: PatchReceiveNoteItem) => void;
  isPending: boolean;
  isReceiveNoteCompleted: boolean;
}

export function FastTrackIssuesCard(props: FastTrackIssuesCardProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [prevIsCollapsedAll, setPrevIsCollapsedAll] = useState(props.isCollapsedAll);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  if (props.isCollapsedAll !== prevIsCollapsedAll) {
    setPrevIsCollapsedAll(props.isCollapsedAll);
    setCollapsed(props.isCollapsedAll);
  }

  const columns: Column[] = [
    {
      width: 10,
      element: <FastTrackTableHeader></FastTrackTableHeader>,
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.ftNumber')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.blockedBy')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.ftDate')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.ftQuantity')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.note')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.orderNumber')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.orderDate')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.vehicle')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>
          {i18n.t('entity.warehouse.labels.licencePlate')}
        </FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.customer')}</FastTrackTableHeader>
      ),
    },
  ];

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [collapsed]);

  const handleSetCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleOnAccept = () => {
    props.onAcceptIssue({
      receiveNoteItemId: props.receiveNoteItem.receiveNoteItemId,
      isAccepted: !props.receiveNoteItem.isAccepted,
      fastTrackItem: props.receiveNoteItem.fastTruckIssues.map((fastTruckIssue) => {
        return {
          fastTrackItemId: fastTruckIssue.fastTruckItemId,
          isSelected: fastTruckIssue.isSelected,
        };
      }),
    });
  };

  const handleOnAcceptIssue = (updatedFastTrackItem: FastTruckIssue) => {
    const updatedFastTrackItems = props.receiveNoteItem.fastTruckIssues.map((fastTruckIssue) => {
      if (fastTruckIssue.fastTruckItemId === updatedFastTrackItem.fastTruckItemId) {
        return {
          fastTrackItemId: fastTruckIssue.fastTruckItemId,
          isSelected: !fastTruckIssue.isSelected,
        };
      }

      return {
        fastTrackItemId: fastTruckIssue.fastTruckItemId,
        isSelected: fastTruckIssue.isSelected,
      };
    });

    props.onAcceptIssue({
      receiveNoteItemId: props.receiveNoteItem.receiveNoteItemId,
      isAccepted: props.receiveNoteItem.isAccepted,
      fastTrackItem: updatedFastTrackItems,
    });
  };

  return (
    <Box ref={containerRef} position="relative">
      <Card key={props.receiveNoteItem.receiveNoteItemId} variant="inlineGrey">
        <ReceiveNoteInformationRow
          receiveNoteItem={props.receiveNoteItem}
          onToggleCollapse={() => handleSetCollapse()}
          onAccept={handleOnAccept}
          isCollapsed={collapsed}
          isReceiveNoteCompleted={props.isReceiveNoteCompleted}
        />
        <Space vertical={4} />
        <Show when={!collapsed}>
          <Show when={props.receiveNoteItem.fastTruckIssues.length > 0}>
            <Box
              overflowX="auto"
              width={containerWidth ? getStepsFromPixels(containerWidth - 48) : '100%'}
            >
              <Table variant="bordered" columns={columns} tableLayout="fixed">
                {props.receiveNoteItem.fastTruckIssues.map((fastTruckIssue, index) => (
                  <FastTrackIssueTableRow
                    key={index}
                    fastTruckIssue={fastTruckIssue}
                    onAcceptIssue={handleOnAcceptIssue}
                    isReceiveNoteCompleted={props.isReceiveNoteCompleted}
                  />
                ))}
              </Table>
            </Box>
          </Show>
          <Show when={props.receiveNoteItem.fastTruckIssues.length === 0}>
            <Center height={10}>
              <Heading alternative size={5}>
                {i18n.t('entity.warehouse.labels.noFastTrackIssues')}
              </Heading>
            </Center>
          </Show>
        </Show>
      </Card>
      <Show when={props.isPending}>
        <Spinner variant="overlay" />
      </Show>
    </Box>
  );
}
